import { Injectable } from '@angular/core';
import {
    TemporaryLinkControllerService,
    TemporaryLinkControllerValidate200Response,
    TemporaryLink,
} from '@api-swagger-client/index';
import { Observable, tap, catchError, throwError } from 'rxjs';

export interface CreateLinkRequest {
    linkType: string;
    userId?: number;
    referenceId?: string;
    expiresInHours?: number;
}

export interface ValidateLinkResponse {
    valid: boolean;
    link?: TemporaryLink;
    message?: string;
}

export interface LinkStats {
    total: number;
    used: number;
    expired: number;
    active: number;
}

@Injectable({
    providedIn: 'root',
})
export class TemporalLinkService {
    constructor(
        private temporaryLinkControllerService: TemporaryLinkControllerService
    ) {}

    /**
     * Crear un nuevo link temporal
     */
    createTemporalLink = (
        linkData: CreateLinkRequest
    ): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerCreate(linkData)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error creating link:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Validar un link temporal sin consumirlo
     */
    validateTemporalLink = (
        token: string
    ): Observable<TemporaryLinkControllerValidate200Response> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerValidate(token)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error validating link:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Usar/consumir un link temporal
     */
    useTemporalLink = (token: string): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerUseLink(token)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error using link:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Obtener links de un usuario
     */
    getUserLinks = (
        userId: string,
        linkType?: string
    ): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerFindByUser(userId, linkType)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error fetching user links:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Invalidar un link
     */
    invalidateLink = (linkId: string): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerInvalidate(linkId)
            .pipe(
                tap(() => {
                }),
                catchError((error) => {
                    console.error('Error invalidating link:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Eliminar un link
     */
    deleteLink = (linkId: string): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerDeleteById(linkId)
            .pipe(
                tap(() => {
                }),
                catchError((error) => {
                    console.error('Error deleting link:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Obtener todos los links con filtros
     */
    getAllLinks = (filters?: any): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerFind(filters)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error fetching links:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Contar links
     */
    countLinks = (filters?: any): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerCount(filters)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error counting links:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Obtener link por ID
     */
    getLinkById = (linkId: string): Observable<any> => {
        return this.temporaryLinkControllerService
            .temporaryLinkControllerFindById(linkId)
            .pipe(
                tap((response) => {
                }),
                catchError((error) => {
                    console.error('Error fetching link by ID:', error);
                    return throwError(() => error);
                })
            );
    };

    // Métodos de conveniencia para tipos específicos de links

    /**
     * Crear link de verificación de email
     */
    createEmailVerificationLink = (
        userId: number,
        referenceId?: string,
        expiresInHours: number = 24
    ): Observable<any> => {
        return this.createTemporalLink({
            linkType: 'email-verification',
            userId,
            referenceId,
            expiresInHours,
        });
    };

    /**
     * Crear link de reset de password
     */
    createPasswordResetLink = (
        userId: number,
        expiresInHours: number = 2
    ): Observable<any> => {
        return this.createTemporalLink({
            linkType: 'password-reset',
            userId,
            expiresInHours,
        });
    };

    /**
     * Crear link de invitación
     */
    createInvitationLink = (
        referenceId: string,
        expiresInHours: number = 168 // 7 días
    ): Observable<any> => {
        return this.createTemporalLink({
            linkType: 'invitation',
            referenceId,
            expiresInHours,
        });
    };

    /**
     * Verificar email usando token
     */
    verifyEmail = (token: string): Observable<any> => {
        return this.useTemporalLink(token);
    };

    /**
     * Verificar si un token es válido y obtener información
     */
    checkTokenValidity = (token: string): Observable<any> => {
        return this.validateTemporalLink(token);
    };
}
